<template lang="pug">
v-card.white.pa-6.border-outline.round-2xl.mb-4(outlined)
  .text-md.font-semibold.neutral700--text {{ $t('header') }}
  .text-sm.font-normal.mb-4 {{ $t('header_description') }}
  v-card.pa-4.round-2xl.border-outline(outlined)
    template(v-if="isFetching")
      v-skeleton-loader.px-4(type="list-item-three-line")
    template(v-else)
      template(v-if="isEditingHeader")
        v-form(ref="form")
          .mb-6
            .text-sm.font-semibold {{ $t('company_logo') }}
            .text-sm.font-normal.neutral500--text.mb-2 {{ $t('company_logo_description') }}
            v-row(no-gutters align="center")
              v-col(cols="2")
                v-card(v-if="headerData.resizeImageFileUrl" flat)
                  div(align="center")
                    v-img(:src="headerData.resizeImageFileUrl" contain max-height="64" max-width="64")
                      template(v-slot:placeholder)
                        v-row(class="fill-height ma-0" align="center" justify="center")
                          v-progress-circular(indeterminate color="grey")
                div.neutral300.round-lg.d-flex.align-center.justify-center.pa-4(v-else)
                  v-icon(color="neutral500") $yzi-image-01
              v-col
                div.file-change-layout.ml-4
                  v-btn.button-sm(outlined) {{ $t('upload') }}
                    input.input-change-layout(type="file" @change="onFileChange" accept="image/*" style="width: 92px !important")
          .text-sm.font-semibold {{ $t('address') }}
          .mb-6
            div.text-xs.font-semibold.neutral500--text.mt-3 {{$t('address_1.label')}}
            v-text-field.text-sm.mt-1.round-xl(v-model="headerData.address.address_1" :placeholder="$t('address_1.placeholder')" outlined dense :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto")
            div.text-xs.font-semibold.neutral500--text.mt-3 {{$t('address_2.label')}}
            v-text-field.text-sm.mt-1.round-xl(v-model="headerData.address.address_2" :placeholder="$t('address_2.placeholder')" outlined dense hide-details="auto")
            div.text-xs.font-semibold.neutral500--text.mt-3 {{$t('postcode.label')}}
            v-text-field.text-sm.mt-1.round-xl(v-model="headerData.address.postcode" :placeholder="$t('postcode.placeholder')" outlined dense :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" @keyup="fetchPostcodes(headerData.address.postcode)")
            div.text-xs.font-semibold.neutral500--text.mt-3 {{$t('city.label')}}
            v-text-field.text-sm.mt-1.round-xl(v-model="headerData.address.city" :placeholder="$t('city.placeholder')" outlined dense :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
            div.text-xs.font-semibold.neutral500--text.mt-3 {{$t('state.label')}}
            v-text-field.text-sm.mt-1.round-xl(v-model="headerData.address.state" :placeholder="$t('state.placeholder')" outlined dense :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
            div.text-xs.font-semibold.neutral500--text.mt-3 {{$t('country.label')}}
            v-text-field.text-sm.mt-1.round-xl(v-model="headerData.address.country" :placeholder="$t('country.placeholder')" outlined dense :rules="emptyAddress ? [] : rules.not_empty" hide-details="auto" :loading="isFetchingAddress")
          v-row.mb-6(no-gutters)
            v-col.mr-4
              .text-sm.font-semibold.neutral700--text.mb-1 {{ $t('clinic_name') }}
              v-text-field.text-sm.round-xl(v-model="headerData.name" outlined :placeholder="$t('clinic_name_placeholder')" hide-details="auto" dense :rules="emptyAddress ? [] : rules.not_empty")
            v-col
              .text-sm.font-semibold.neutral700--text.mb-1 {{ $t('clinic_phone') }}
              v-text-field.text-sm.round-xl(v-model="headerData.phone" outlined :placeholder="$t('clinic_phone_placeholder')" hide-details="auto" dense :rules="emptyAddress ? [] : rules.not_empty")
          .d-flex.justify-end
            v-btn.button-sm.underline.mr-2(color="primary" text @click="onCancelHeader") {{ $t('cancel') }}
            v-btn.button-sm(color="primary" depressed :loading="isLoading" :disabled="isEmptyData" @click="onSave") {{ $t('save') }}
      template(v-else)
        v-row(no-gutters)
          v-col(cols="2")
            v-card(v-if="headerData.resizeImageFileUrl" flat)
              div(align="center")
                v-img(:src="headerData.resizeImageFileUrl" contain max-height="64" max-width="64")
                  template(v-slot:placeholder)
                    v-row(class="fill-height ma-0" align="center" justify="center")
                      v-progress-circular(indeterminate color="grey")
            div.neutral300.round-lg.d-flex.flex-column.align-center.justify-center.pa-4.mr-4(v-else)
              v-icon(color="neutral500") $yzi-image-01
              .text-xxs.neutral500--text {{ $t('logo') }}
          v-col(cols="4")
            .text-xs.font-semibold.text-uppercase {{ headerData.name ? headerData.name : $t('clinic_name') }}
            .text-xxs.font-normal {{ emptyAddress ? $t('address') : getAddressInString(headerData.address) }}
            .text-xxs.font-normal {{ headerData.phone ? `Tel: ${headerData.phone}` : $t('tel_number') }}
          v-spacer
          v-col.text-right
            .text-xs.font-semibold.underline.clickable(@click="isEditingHeader = true") {{ isEmptyData ? $t('set_header_now') : $t('edit_header_now') }}
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { resizeImage } from '@/utils'

export default {
  props: {
    headerData: {
      type: Object,
      default: () => {
        return {
          name: null,
          phone: null,
          address: {},
          resizeImageFileUrl: '',
          resizeImageFile: null,
        }
      }
    },
  },
  data() {
    return {
      isFetching: true,
      isFetchingAddress: false,
      id: null,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
      },
      isEditingHeader: false,
      originalHeader: null,
      isLoading: false
    }
  },
  mounted () {
    this.fetchHeader()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    emptyAddress() {
      if (Object.keys(this.headerData.address).length === 0 && !this.headerData.name && !this.headerData.phone) {
        return true
      } else if (Object.values(this.headerData.address).every(val => !val) && !this.headerData.name && !this.headerData.phone) {
        return true
      }
      return false
    },
    isEmptyData () {
      return this.emptyAddress && !this.headerData.resizeImageFileUrl
    },
    params() {
      return {
        page_size: 1,
        company: this.company.id
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onCancelHeader () {
      this.resetData()
      this.isEditingHeader = false
    },
    fetchPostcodes (val) {
      if (!val) {
        return
      }
      if (val.length !== 5) {
        return
      }
      this.isFetchingAddress = true
      this.$axios.get(`/shipping/postcodes?postcode=${this.headerData.address.postcode}`).then((response) => {
        const addresses = response.data
        if (addresses.length > 0 && addresses[0].postcode) {
          this.$set(this.headerData.address, 'city', addresses[0].city)
          this.$set(this.headerData.address, 'state', addresses[0].state)
          this.$set(this.headerData.address, 'country', addresses[0].country)
        }
      })
      .catch(() => {
      })
      .finally(() => {
        this.isFetchingAddress = false
      })
    },
    resetData () {
      if (this.originalHeader) {
        this.headerData.name = this.originalHeader.address?.name
        this.headerData.phone = this.originalHeader.address?.phone
        this.headerData.resizeImageFileUrl = this.originalHeader.logo?.file ?? ''
        this.headerData.resizeImageFile = null
        this.headerData.address = this.sanitizeAddress(this.originalHeader.address) ?? {}
      } else {
        this.headerData.name = null
        this.headerData.phone = null
        this.headerData.resizeImageFileUrl = ''
        this.headerData.resizeImageFile = null
        this.headerData.address = {}
      }
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    async fetchHeader () {
      this.isFetching = true
      try {
        const { data } = await this.$axios.get('/document_template_headers', { params: this.params })
        if (data.count > 0) {
          this.mapData(data)
        }
        this.isFetching = false
      // eslint-disable-next-line no-empty
      } catch (error) {}
    },
    sanitizeAddress (address) {
      if (address) {
        const allowedProperties = ['address_1', 'address_2', 'city', 'postcode', 'state', 'country']
        return this.$options.filters.filterAllowedObjectProperties(address, allowedProperties)
      }
      return null
    },
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const resizedImage = await resizeImage(files[0], 'company_image.jpeg')
      this.headerData.resizeImageFileUrl = resizedImage.dataURL
      this.headerData.resizeImageFile = resizedImage.file
    },
    mapData (data, isList = true) {
      let result
      if (isList) {
        result = data.results[0]
      } else {
        result = data
      }
      const { id, address, logo } = result
      this.id = id
      this.headerData.address = this.sanitizeAddress(address) ?? {}
      this.headerData.name = address?.name ?? null
      this.headerData.phone = address?.phone ?? null
      this.headerData.resizeImageFileUrl = logo?.file ?? ''
      this.originalHeader = result
    },
    async onSave () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      this.isLoading = true
      const payload = new FormData()
      payload.append('company', this.company.id)
      if (this.headerData.name) {
        payload.append('address.name', this.headerData.name)
      }
      if (this.headerData.phone) {
        payload.append('address.phone', this.headerData.phone)
      }
      if (!this.emptyAddress){
        for (let key in this.headerData.address) {
          payload.append(`address.${key}`, this.headerData.address[key])
        }
      }
      if (this.headerData.resizeImageFile) {
        payload.append('logo', this.headerData.resizeImageFile)
      }
      try {
        if (this.id) {
          const { data } = await this.$axios.patch(`/document_template_headers/${this.id}`, payload)
          this.mapData(data, false)
        } else {
          const { data } = await this.$axios.post(`/document_template_headers`, payload)
          this.mapData(data, false)
        }
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
        this.isEditingHeader = false
      } catch (error) {
        // Handle header deleted via yadmin
        if (error.response.status === 500 && error?.response?.data.includes('already exists')) {
          this.showMessage(['error', 'Template header already exsited before. Please contact Yezza Support'])
          this.resetData()
          return
        }
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')]) 
      } finally {
        this.isLoading = false
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.border-outline {
  border: 1px solid #EEEEEE !important;
}
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .combostyle .v-input__icon {
    min-width: 22px;
    width: 22px;
    margin-top: 4px;
  }
}
::v-deep .v-ripple__container {
  display:none !important;
}
.field-outline {
  border: 1px solid var(--v-neutral400) !important;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
</style>

<i18n>
{
  "en": {
    "address_1": {
      "label": "Address Line 1",
      "placeholder": "Enter address line 1"
    },
    "address_2": {
      "label": "Address Line 2",
      "placeholder": "Enter address line 2"
    },
    "postcode": {
      "label": "Postcode",
      "placeholder": "Enter postcode"
    },
    "city": {
      "label": "City",
      "placeholder": "Enter city"
    },
    "state": {
      "label": "State",
      "placeholder": "Enter state"
    },
    "country": {
      "label": "Country",
      "placeholder": "Enter country"
    }
  },
  "id": {
   "address_1": {
      "label": "Nama Jalan, Gedung, No Rumah, RT/RW, Kelurahan",
      "placeholder": "e.g. Jalan Prof. Joko Sutono Sh No.999, RT.14/RW.6, Petogogan, "
    },
    "address_2": {
      "label": "Kecamatan",
      "placeholder": "Kec. Kby. Baru"
    },
    "postcode": {
      "label": "Kode Pos",
      "placeholder": "12160"
    },
    "city": {
      "label": "Kota",
      "placeholder": "Kota Jakarta Selatan"
    },
    "state": {
      "label": "Provinsi",
      "placeholder": "DKI Jakarta"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Indonesia"
    }
  }
}
</i18n>